/* eslint-disable no-use-before-define */

import { createClient } from '@supabase/supabase-js';
import { authenticatedHeadersHash, accessToken, anonToken } from '../../v2_authentication';
import vlog from '../../vlog';
import { buildMockSupabaseClient, loadV2TestMocks } from './v2_test_mocks';

const applyV2TestMocks = (mocks) => {
  if (!mocks) {
    return false;
  }

  if (mocks.profile_id) {
    window.__V2_TEST_MODE__ = true;
    window.V2_PROFILE_ID = mocks.profile_id;
  }

  if (!window.supabaseClient) {
    window.supabaseClient = buildMockSupabaseClient(mocks);
  }

  return true;
};

applyV2TestMocks(loadV2TestMocks());

window.initializeSupabaseClient = async () => { // Make async to await
  if (applyV2TestMocks(loadV2TestMocks())) {
    return;
  }

  if (!window.supabaseClient) {
// https://github.com/supabase/supabase-js/issues/514#issuecomment-1218513303
    const supabaseClient = createClient(window.V2_SUPABASE_API_EXTERNAL_URL, anonToken(), {
      db: {
        schema: window.V2_SUPABASE_API_SCHEMA,
      },
      auth: {
        autoRefreshToken: false,
        detectSessionInUrl: false,
        persistSession: false,
      },
      global: {
        headers: authenticatedHeadersHash(),
      },
    });
    supabaseClient.realtime.setAuth(accessToken());
    window.supabaseClient = supabaseClient;
  }
};

window.v2HealthCheck = async function v2HealthCheck() {
  if (window.RAILS_ENV === 'test') {
    // Fail health check if hash contains the test failure flag
    if (window.location.hash.includes('__test__fail__health__check__')) {
      return false;
    }
    return true;
  }

  const accessTok = accessToken();
  if (!accessTok) {
    return 'no_token';
  }

  try {
    const response = await fetch(`${window.V2_URL}/health?exclude=kafka_feed_lag_v1,kafka_feed_lag_app,sidekiq_queue_latency`, { method: 'GET' });
    return response.ok;
  } catch (e) {
    return false;
  }
};

window.v2ProfileId = async function V2ProfileId() {
  if (window.V2_PROFILE_ID) {
    return window.V2_PROFILE_ID;
  }

  if (applyV2TestMocks(loadV2TestMocks())) {
    return window.V2_PROFILE_ID;
  }

  // If not cached, fetch from the API
  const v2Ids = await _fetchV2Ids(window.CURRENT_USER_JSON.id);
  if (v2Ids) {
    return v2Ids.profileId;
  }

  vlog.error('Error fetching V2 profile ID.');
  return null; // or handle the error as needed
};

window.v2UserId = async function V2UserId() {
  if (window.V2_USER_ID) {
    return window.V2_USER_ID;
  }

  // If not cached, fetch from the API
  const v2Ids = await _fetchV2Ids(window.CURRENT_USER_JSON.id);
  if (v2Ids) {
    return v2Ids.accountUserId;
  }

  vlog.error('Error fetching V2 user ID.');
  return null; // or handle the error as needed
};

window.v2UserProfileSlug = async function V2UserId() {
  if (window.V2_USER_PROFILE_SLUG) {
    return window.V2_USER_PROFILE_SLUG;
  }

  // If not cached, fetch from the API
  const v2Ids = await _fetchV2Ids(window.CURRENT_USER_JSON.id);
  if (v2Ids) {
    return v2Ids.slug;
  }

  vlog.error('Error fetching V2 user ID.');
  return null; // or handle the error as needed
};

const _fetchV2Ids = async function _fetchV2Ids(userId) {
  try {
    // Ensure client is initialized - await it as initializeSupabaseClient is now async
    await window.initializeSupabaseClient();

    const { data, error: fetchError } = await window.supabaseClient
      .from('user_profiles')
      .select('profile_id, user_id, slug')
      .eq('user_migrate_id', userId)
      .single(); // We expect only one profile per user

    if (fetchError) {
      // It's possible the user doesn't have a V2 profile yet, or the session isn't valid
      // Log differently based on error type if needed
      if (fetchError.message.includes('JWT')) {
        vlog.warn('Error fetching user profile (likely invalid session):', fetchError.message);
      } else {
        vlog.error('Error fetching user profile:', fetchError);
      }
      return null;
    }

    if (data?.profile_id && data?.user_id) {
      // Cache the values on the window object
      window.V2_PROFILE_ID = data.profile_id;
      window.V2_USER_ID = data.user_id;
      window.V2_USER_PROFILE_SLUG = data.slug;
      return { profileId: data.profile_id, accountUserId: data.user_id, slug: data.slug };
    }

    vlog.warn(`V2 IDs not found for V1 user ID ${userId}!`); // Changed to warn
    return null;
  } catch (fetchError) {
    vlog.error('Error fetching V2 IDs', fetchError);
    return null;
  }
};
